import { useEffect, useState } from 'react';
import {
  Inbox,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Mail,
  MessageSquare,
  Phone,
  Globe,
  Send,
  Plus,
  Filter,
  ChevronDown,
  MoreHorizontal,
  type LucideIcon,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useAuthStore } from '../lib/auth-store';
import { apiFetch, apiFetchPaginated } from '../lib/api';
import { formatRelative } from '../lib/format';
import { Link } from 'react-router-dom';
import type { Ticket, TicketChannel, TicketPriority, TicketStatus } from '@vipsoft/shared-types';
import { BotHealthBanner } from '../components/BotHealthBanner';

interface OverviewKpis {
  open_tickets: number;
  open_tickets_delta_pct: number;
  at_risk: number;
  at_risk_delta_abs: number;
  avg_first_response_minutes: number | null;
  avg_first_response_delta_pct: number;
  csat_average: number | null;
  csat_count: number;
  volume_last_7_days: Array<{ day: string; received: number; resolved: number }>;
  by_channel_last_7_days: Array<{ channel: string; count: number }>;
}

interface Kpi {
  label: string;
  value: string;
  delta: string;
  up: boolean;
  sub: string;
  icon: LucideIcon;
  tint: 'blue' | 'amber' | 'indigo' | 'emerald';
  invertGoodness?: boolean;
}

export default function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const greetingName = user?.name?.split(' ')[0] ?? 'agente';
  const [overview, setOverview] = useState<OverviewKpis | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<OverviewKpis>('/reports/overview')
      .then((data) => setOverview(data))
      .catch(() => setOverview(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageHeader name={greetingName} />
      <BotHealthBanner />
      <KpiRow overview={overview} loading={loading} />
      <ChartsRow overview={overview} loading={loading} />
      <RecentTicketsTable />
    </>
  );
}

function PageHeader({ name }: { name: string }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <p className="text-sm text-slate-500">
          {greeting}, {name} 👋
        </p>
        <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
          Resumen de hoy
        </h1>
      </div>

      <div className="flex items-center gap-2">
        <button className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
          <Filter size={14} />
          Últimos 7 días
          <ChevronDown size={14} className="text-slate-400" />
        </button>
        <button className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white">
          <Plus size={14} />
          Nuevo ticket
        </button>
      </div>
    </div>
  );
}

const TINT_BG: Record<Kpi['tint'], string> = {
  blue: 'bg-blue-50 text-blue-600',
  amber: 'bg-amber-50 text-amber-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  emerald: 'bg-emerald-50 text-emerald-600',
};

function formatDelta(value: number, suffix = '%'): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${Math.round(value)}${suffix}`;
}

function KpiRow({
  overview,
  loading,
}: {
  overview: OverviewKpis | null;
  loading: boolean;
}) {
  const kpis: Kpi[] = overview
    ? [
        {
          label: 'Tickets abiertos',
          value: String(overview.open_tickets),
          delta: formatDelta(overview.open_tickets_delta_pct),
          up: overview.open_tickets_delta_pct >= 0,
          sub: 'vs semana pasada',
          icon: Inbox,
          tint: 'blue',
        },
        {
          label: 'En riesgo de SLA',
          value: String(overview.at_risk),
          delta: formatDelta(overview.at_risk_delta_abs, ''),
          up: overview.at_risk_delta_abs >= 0,
          sub: 'vs ayer',
          icon: AlertTriangle,
          tint: 'amber',
          invertGoodness: true,
        },
        {
          label: 'Tiempo medio respuesta',
          value:
            overview.avg_first_response_minutes !== null
              ? `${overview.avg_first_response_minutes} min`
              : '—',
          delta: formatDelta(overview.avg_first_response_delta_pct),
          up: overview.avg_first_response_delta_pct >= 0,
          sub: 'vs semana pasada',
          icon: Clock,
          tint: 'indigo',
          invertGoodness: true,
        },
        {
          label: 'CSAT promedio',
          value:
            overview.csat_average !== null
              ? `${overview.csat_average.toFixed(1)} / 5`
              : 'Sin datos',
          delta: overview.csat_count > 0 ? `${overview.csat_count} respuestas` : '—',
          up: true,
          sub: 'últimos 30 días',
          icon: CheckCircle2,
          tint: 'emerald',
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl border border-slate-200 p-5 h-32 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (kpis.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-8 text-sm text-amber-800">
        No se pudieron cargar las métricas. Reintentá en unos segundos.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
      {kpis.map((k) => {
        const Trend = k.up ? ArrowUpRight : ArrowDownRight;
        // good si subió cuando subir es bueno, o bajó cuando bajar es bueno
        const good = k.invertGoodness ? !k.up : k.up;
        return (
          <div
            key={k.label}
            className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-sm transition"
          >
            <div className="flex items-start justify-between">
              <div className={`h-9 w-9 rounded-lg grid place-items-center ${TINT_BG[k.tint]}`}>
                <k.icon size={16} />
              </div>
              <span
                className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${
                  good ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'
                }`}
              >
                <Trend size={12} />
                {k.delta}
              </span>
            </div>
            <div className="mt-4">
              <div className="text-sm text-slate-500">{k.label}</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                {k.value}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400">{k.sub}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CHANNEL_LABEL: Record<string, string> = {
  api: 'API',
  email: 'Email',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  widget: 'Widget',
};

function ChartsRow({
  overview,
  loading,
}: {
  overview: OverviewKpis | null;
  loading: boolean;
}) {
  const volumeData = overview?.volume_last_7_days ?? [];
  const channelData = (overview?.by_channel_last_7_days ?? []).map((r) => ({
    canal: CHANNEL_LABEL[r.channel] ?? r.channel,
    n: r.count,
  }));

  if (loading) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-8">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 h-80 animate-pulse" />
        <div className="bg-white rounded-2xl border border-slate-200 p-5 h-80 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-8">
      <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Volumen de tickets</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Recibidos vs. resueltos en los últimos 7 días
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <LegendDot color="#2563EB" label="Recibidos" />
            <LegendDot color="#10B981" label="Resueltos" />
          </div>
        </div>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={volumeData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gTickets" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gResueltos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E2E8F0' }} />
              <Area type="monotone" dataKey="received" stroke="#2563EB" strokeWidth={2} fill="url(#gTickets)" name="Recibidos" />
              <Area type="monotone" dataKey="resolved" stroke="#10B981" strokeWidth={2} fill="url(#gResueltos)" name="Resueltos" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900">Tickets por canal</h3>
        <p className="text-xs text-slate-500 mt-0.5">Distribución de los últimos 7 días</p>
        <div className="h-64 mt-4">
          {channelData.length === 0 ? (
            <div className="h-full grid place-items-center text-xs text-slate-400">
              Aún no hay tickets registrados.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={channelData} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="canal" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                <Bar dataKey="n" radius={[6, 6, 0, 0]} fill="#2563EB" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-500">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

const CHANNEL_ICONS: Record<TicketChannel, { Icon: LucideIcon; color: string }> = {
  email: { Icon: Mail, color: 'text-blue-600 bg-blue-50' },
  whatsapp: { Icon: Phone, color: 'text-emerald-600 bg-emerald-50' },
  telegram: { Icon: Send, color: 'text-sky-600 bg-sky-50' },
  widget: { Icon: Globe, color: 'text-violet-600 bg-violet-50' },
  api: { Icon: MessageSquare, color: 'text-slate-600 bg-slate-100' },
};

const STATUS_STYLE: Record<TicketStatus, string> = {
  new: 'text-slate-700 bg-slate-100',
  open: 'text-blue-700 bg-blue-50',
  in_progress: 'text-indigo-700 bg-indigo-50',
  waiting_customer: 'text-amber-700 bg-amber-50',
  resolved: 'text-emerald-700 bg-emerald-50',
  closed: 'text-slate-500 bg-slate-100',
  reopened: 'text-orange-700 bg-orange-50',
};

const PRIORITY_STYLE: Record<TicketPriority, string> = {
  urgent: 'text-rose-700 bg-rose-50 ring-rose-200',
  high: 'text-orange-700 bg-orange-50 ring-orange-200',
  normal: 'text-slate-700 bg-slate-50 ring-slate-200',
  low: 'text-slate-500 bg-slate-50 ring-slate-200',
};

const STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Nuevo',
  open: 'Abierto',
  in_progress: 'En proceso',
  waiting_customer: 'Esperando cliente',
  resolved: 'Resuelto',
  closed: 'Cerrado',
  reopened: 'Reabierto',
};

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baja',
};

function RecentTicketsTable() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetchPaginated<Ticket>('/tickets?per_page=8&sort=-created_at')
      .then((res) => setTickets(res.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Tickets recientes</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Últimas solicitudes recibidas en todos los canales
          </p>
        </div>
        <Link
          to="/admin/tickets"
          className="text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          Ver todos →
        </Link>
      </div>

      {loading && (
        <div className="px-5 py-10 text-sm text-slate-500 text-center">Cargando tickets…</div>
      )}
      {error && (
        <div className="px-5 py-10 text-sm text-rose-600 text-center">{error}</div>
      )}
      {!loading && !error && tickets.length === 0 && (
        <div className="px-5 py-10 text-sm text-slate-500 text-center">
          Todavía no hay tickets registrados.
        </div>
      )}

      {!loading && tickets.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-medium">Ticket</th>
                <th className="px-5 py-3 font-medium">Canal</th>
                <th className="px-5 py-3 font-medium">Estado</th>
                <th className="px-5 py-3 font-medium">Prioridad</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.map((t) => {
                const channel = CHANNEL_ICONS[t.channel];
                const Icon = channel.Icon;
                return (
                  <tr key={t.id} className="hover:bg-slate-50 transition">
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/tickets/${t.code}`}
                        className="font-medium text-slate-900 leading-tight truncate max-w-[280px] block hover:text-blue-600"
                      >
                        {t.subject}
                      </Link>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {t.code} · {formatRelative(t.created_at)}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs ${channel.color}`}
                      >
                        <Icon size={12} />
                        <span className="capitalize">{t.channel}</span>
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[t.status]}`}
                      >
                        {STATUS_LABEL[t.status]}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ring-1 ${PRIORITY_STYLE[t.priority]}`}
                      >
                        {PRIORITY_LABEL[t.priority]}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link
                        to={`/admin/tickets/${t.code}`}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 inline-flex"
                      >
                        <MoreHorizontal size={16} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
